import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import maplibregl from 'maplibre-gl';

export class TropicalLayer extends BaseProduct {
  public readonly id: ProductId = 'tropical';
  private static instance: TropicalLayer | null = null;
  private pinnedPopup: maplibregl.Popup | null = null;
  private hoverPopup: maplibregl.Popup | null = null;
  private mapEventsBound: boolean = false;
  private showForecastRadii: boolean = false;

  constructor(map: maplibregl.Map, layerManager: any) {
    super(map, layerManager);
    TropicalLayer.instance = this;
    if (typeof window !== 'undefined') {
      (window as any).toggleTropicalForecastRadii = (show: boolean) => {
        TropicalLayer.getInstance()?.setShowForecastRadii(show);
      };
      (window as any).isTropicalForecastRadiiEnabled = () => {
        return TropicalLayer.getInstance()?.getShowForecastRadii() ?? false;
      };
    }
  }

  public static getInstance(): TropicalLayer | null {
    return TropicalLayer.instance;
  }

  public setShowForecastRadii(show: boolean): void {
    this.showForecastRadii = show;
    this.updateRadiiFilter();
  }

  public getShowForecastRadii(): boolean {
    return this.showForecastRadii;
  }

  private getRadiiFilter(): any {
    if (this.showForecastRadii) {
      return ['==', ['get', 'feature_type'], 'wind_radii'];
    }
    return ['all', ['==', ['get', 'feature_type'], 'wind_radii'], ['==', ['get', 'is_initial'], true]];
  }

  private updateRadiiFilter(): void {
    const filter = this.getRadiiFilter();
    if (this.map.getLayer('tropical-radii-fill')) {
      this.map.setFilter('tropical-radii-fill', filter);
    }
    if (this.map.getLayer('tropical-radii-stroke')) {
      this.map.setFilter('tropical-radii-stroke', filter);
    }
  }

  protected onActivate(): void {
    this.refresh();
    this.bindMapEvents();
  }

  protected onDeactivate(): void {
    this.removeLayers();
    this.unbindMapEvents();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(): void {
    if (!this.active) return;
    this.setupLayers();
    this.updateVisibility();
  }

  private getDataUrl(): string {
    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = 'https://auth.skyskout.com/';
    } else if (apiServer === '') {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const versionParam = manifest?.generated_at ? `?v=${encodeURIComponent(manifest.generated_at)}` : `?t=${Date.now()}`;

    return `${apiServer}${tileRoot}/observed/tropical/tropical_active.geojson${versionParam}`;
  }

  private setupLayers(): void {
    const dataUrl = this.getDataUrl();
    const sourceId = 'tropical-source';

    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: dataUrl
      });
    } else {
      const source = this.map.getSource(sourceId) as maplibregl.GeoJSONSource;
      if (source && typeof source.setData === 'function') {
        source.setData(dataUrl);
      }
    }

    const layers = this.map.getStyle()?.layers;
    let beforeId: string | undefined = undefined;
    if (layers) {
      const firstSymbol = layers.find(l => l.type === 'symbol');
      if (firstSymbol) beforeId = firstSymbol.id;
    }

    const opacity = getLayerOpacity(this.id, 1.0);

    // 1. Genesis Areas (Disturbances) Fill
    if (!this.map.getLayer('tropical-genesis-fill')) {
      this.map.addLayer({
        id: 'tropical-genesis-fill',
        type: 'fill',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'genesis_area'],
        paint: {
          'fill-color': ['coalesce', ['get', 'fill_color'], '#eab308'],
          'fill-opacity': 0.28 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-genesis-fill', 'fill-opacity', 0.28 * opacity);
    }

    // 2. Genesis Areas Stroke
    if (!this.map.getLayer('tropical-genesis-stroke')) {
      this.map.addLayer({
        id: 'tropical-genesis-stroke',
        type: 'line',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'genesis_area'],
        paint: {
          'line-color': ['coalesce', ['get', 'stroke_color'], '#a16207'],
          'line-width': 2.0,
          'line-dasharray': [3, 2],
          'line-opacity': 0.85 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-genesis-stroke', 'line-opacity', 0.85 * opacity);
    }

    // 3. 5-Day Cone of Uncertainty Fill
    if (!this.map.getLayer('tropical-cone-fill')) {
      this.map.addLayer({
        id: 'tropical-cone-fill',
        type: 'fill',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'track_cone'],
        paint: {
          'fill-color': '#ffffff',
          'fill-opacity': 0.24 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-cone-fill', 'fill-opacity', 0.24 * opacity);
    }

    // 4. 5-Day Cone Stroke
    if (!this.map.getLayer('tropical-cone-stroke')) {
      this.map.addLayer({
        id: 'tropical-cone-stroke',
        type: 'line',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'track_cone'],
        paint: {
          'line-color': '#e2e8f0',
          'line-width': 1.6,
          'line-dasharray': [4, 2],
          'line-opacity': 0.75 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-cone-stroke', 'line-opacity', 0.75 * opacity);
    }

    // 5. Wind Radii Fill (Current Wind Field by default, optionally all forecast radii)
    if (!this.map.getLayer('tropical-radii-fill')) {
      this.map.addLayer({
        id: 'tropical-radii-fill',
        type: 'fill',
        source: sourceId,
        filter: this.getRadiiFilter(),
        paint: {
          'fill-color': ['coalesce', ['get', 'fill_color'], '#eab308'],
          'fill-opacity': 0.32 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-radii-fill', 'fill-opacity', 0.32 * opacity);
      this.map.setFilter('tropical-radii-fill', this.getRadiiFilter());
    }

    // 6. Wind Radii Stroke
    if (!this.map.getLayer('tropical-radii-stroke')) {
      this.map.addLayer({
        id: 'tropical-radii-stroke',
        type: 'line',
        source: sourceId,
        filter: this.getRadiiFilter(),
        paint: {
          'line-color': ['coalesce', ['get', 'stroke_color'], '#ca8a04'],
          'line-width': 1.2,
          'line-opacity': 0.85 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-radii-stroke', 'line-opacity', 0.85 * opacity);
      this.map.setFilter('tropical-radii-stroke', this.getRadiiFilter());
    }

    // 7. Coastal Watches & Warnings
    if (!this.map.getLayer('tropical-ww-line')) {
      this.map.addLayer({
        id: 'tropical-ww-line',
        type: 'line',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'watch_warning'],
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#ef4444'],
          'line-width': ['coalesce', ['get', 'width'], 3.2],
          'line-opacity': 0.95 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-ww-line', 'line-opacity', 0.95 * opacity);
    }

    // 8. Forecast Track Centerline
    if (!this.map.getLayer('tropical-track-line')) {
      this.map.addLayer({
        id: 'tropical-track-line',
        type: 'line',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'track_line'],
        paint: {
          'line-color': '#0f172a',
          'line-width': 3.2,
          'line-opacity': 0.95 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('tropical-track-line', 'line-opacity', 0.95 * opacity);
    }

    // 9. Forecast Track Points (Circle Background)
    if (!this.map.getLayer('tropical-track-points')) {
      this.map.addLayer({
        id: 'tropical-track-points',
        type: 'circle',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'track_point'],
        paint: {
          'circle-radius': 9.5,
          'circle-color': ['coalesce', ['get', 'category_color'], '#0284c7'],
          'circle-stroke-width': 2.0,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.95 * opacity
        }
      });
    } else {
      this.map.setPaintProperty('tropical-track-points', 'circle-opacity', 0.95 * opacity);
    }

    // 10. Forecast Track Point Symbols (e.g. 1, 2, 3, 4, 5, S, D)
    if (!this.map.getLayer('tropical-track-points-symbol')) {
      this.map.addLayer({
        id: 'tropical-track-points-symbol',
        type: 'symbol',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'track_point'],
        layout: {
          'text-field': ['coalesce', ['get', 'short_code'], ['get', 'dvlbl'], '•'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'text-color': ['coalesce', ['get', 'category_text_color'], '#ffffff']
        }
      });
    }

    // 11. Forecast Track Time Labels (e.g. "5 AM Sat", "5 PM Sat")
    if (!this.map.getLayer('tropical-track-time-labels')) {
      this.map.addLayer({
        id: 'tropical-track-time-labels',
        type: 'symbol',
        source: sourceId,
        filter: ['==', ['get', 'feature_type'], 'track_point'],
        layout: {
          'text-field': ['get', 'valid_time_lbl'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-variable-anchor': ['left', 'right', 'top', 'bottom'],
          'text-radial-offset': 1.15,
          'text-justify': 'auto',
          'text-optional': true
        },
        paint: {
          'text-color': '#f8fafc',
          'text-halo-color': '#0f172a',
          'text-halo-width': 2.0,
          'text-opacity': 0.95 * opacity
        }
      });
    } else {
      this.map.setPaintProperty('tropical-track-time-labels', 'text-opacity', 0.95 * opacity);
    }
  }

  private updateVisibility(): void {
    const visibility = this.active ? 'visible' : 'none';
    const layerIds = [
      'tropical-genesis-fill',
      'tropical-genesis-stroke',
      'tropical-cone-fill',
      'tropical-cone-stroke',
      'tropical-radii-fill',
      'tropical-radii-stroke',
      'tropical-ww-line',
      'tropical-track-line',
      'tropical-track-points',
      'tropical-track-points-symbol',
      'tropical-track-time-labels'
    ];
    for (const lid of layerIds) {
      if (this.map.getLayer(lid)) {
        this.map.setLayoutProperty(lid, 'visibility', visibility);
      }
    }
  }

  private removeLayers(): void {
    if (this.pinnedPopup) {
      this.pinnedPopup.remove();
      this.pinnedPopup = null;
    }
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
    const layerIds = [
      'tropical-track-time-labels',
      'tropical-track-points-symbol',
      'tropical-track-points',
      'tropical-track-line',
      'tropical-ww-line',
      'tropical-radii-stroke',
      'tropical-radii-fill',
      'tropical-cone-stroke',
      'tropical-cone-fill',
      'tropical-genesis-stroke',
      'tropical-genesis-fill'
    ];
    for (const lid of layerIds) {
      if (this.map.getLayer(lid)) {
        this.map.removeLayer(lid);
      }
    }
    if (this.map.getSource('tropical-source')) {
      this.map.removeSource('tropical-source');
    }
  }

  private bindMapEvents(): void {
    if (this.mapEventsBound) return;
    this.mapEventsBound = true;

    const interactiveLayers = [
      'tropical-track-points',
      'tropical-track-points-symbol',
      'tropical-genesis-fill',
      'tropical-ww-line',
      'tropical-radii-fill'
    ];

    for (const lid of interactiveLayers) {
      this.map.on('mouseenter', lid, () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', lid, () => {
        this.map.getCanvas().style.cursor = '';
        if (this.hoverPopup && !this.pinnedPopup) {
          this.hoverPopup.remove();
          this.hoverPopup = null;
        }
      });
      this.map.on('mousemove', lid, (e) => {
        if (this.pinnedPopup) return;
        if (!e.features || e.features.length === 0) return;
        const feat = e.features[0];
        const content = this.renderPopupContent(feat.properties || {});
        if (!this.hoverPopup) {
          this.hoverPopup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'tropical-tooltip-popup'
          });
        }
        this.hoverPopup.setLngLat(e.lngLat).setHTML(content).addTo(this.map);
      });
      this.map.on('click', lid, (e) => {
        if (!e.features || e.features.length === 0) return;
        const feat = e.features[0];
        if (this.hoverPopup) {
          this.hoverPopup.remove();
          this.hoverPopup = null;
        }
        if (this.pinnedPopup) {
          this.pinnedPopup.remove();
        }
        const content = this.renderPopupContent(feat.properties || {}, true);
        this.pinnedPopup = new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true,
          className: 'tropical-pinned-popup'
        })
          .setLngLat(e.lngLat)
          .setHTML(content)
          .addTo(this.map);
        this.pinnedPopup.on('close', () => {
          this.pinnedPopup = null;
        });
      });
    }
  }

  private unbindMapEvents(): void {
    if (!this.mapEventsBound) return;
    this.mapEventsBound = false;
    this.map.getCanvas().style.cursor = '';
  }

  private renderPopupContent(props: any, isPinned = false): string {
    const fType = props.feature_type;

    if (fType === 'track_point') {
      const name = props.storm_name || 'Tropical Cyclone';
      const timeLbl = props.valid_time_lbl || '';
      const fullTime = props.forecast_time_full || '';
      const tau = props.tau_hr !== undefined ? `+${props.tau_hr}h` : '';
      const badge = props.category_badge || 'TC';
      const badgeColor = props.category_color || '#0284c7';
      const badgeTextColor = props.category_text_color || '#ffffff';
      const catName = props.category_name || (props.development ? `${props.development}` : 'Tropical Cyclone');
      const windKt = props.max_wind_kt || 0;
      const windMph = props.max_wind_mph || Math.round(windKt * 1.15078);
      const gustKt = props.gust_kt || 0;
      const gustMph = props.gust_mph || Math.round(gustKt * 1.15078);
      const pressure = props.pressure_mb ? `${props.pressure_mb} mb` : 'N/A';
      const dvlp = props.development || '';

      return `
        <div style="font-family: inherit; font-size: 13px; color: #f8fafc; line-height: 1.4; padding: 2px; min-width: 210px;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; gap: 8px;">
            <span style="font-weight: 800; font-size: 13px; color: #38bdf8;">
              🌀 ${name}
            </span>
            <span style="background: ${badgeColor}; color: ${badgeTextColor}; font-weight: 800; font-size: 11px; padding: 2px 7px; border-radius: 4px; white-space: nowrap; border: 1px solid rgba(255,255,255,0.2);">
              ${badge}
            </span>
          </div>

          <div style="font-size: 13px; font-weight: 800; color: ${badgeColor}; margin-bottom: 5px;">
            ${catName}
          </div>

          <div style="font-size: 11px; color: #cbd5e1; margin-bottom: 6px;">
            <span style="color: #94a3b8;">Forecast Time:</span> <strong>${timeLbl}</strong> ${tau ? `<span style="color: #64748b;">(${tau})</span>` : ''}
            ${fullTime && fullTime !== timeLbl ? `<div style="font-size: 10px; color: #64748b;">${fullTime}</div>` : ''}
          </div>

          <div style="background: rgba(15, 23, 42, 0.6); padding: 6px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.08); margin-bottom: 6px;">
            <div style="font-size: 11px; color: #e2e8f0; display: flex; justify-content: space-between;">
              <span style="color: #94a3b8;">Max Sustained:</span>
              <strong>${windKt} kt (${windMph} mph)</strong>
            </div>
            <div style="font-size: 11px; color: #e2e8f0; display: flex; justify-content: space-between; margin-top: 2px;">
              <span style="color: #94a3b8;">Peak Gusts:</span>
              <strong>${gustKt} kt (${gustMph} mph)</strong>
            </div>
          </div>

          <div style="font-size: 11px; color: #94a3b8; display: flex; justify-content: space-between; border-top: 1px solid rgba(255,255,255,0.12); padding-top: 4px;">
            <span>MSLP: <strong style="color: #f1f5f9;">${pressure}</strong></span>
            ${dvlp ? `<span>Status: <strong style="color: #cbd5e1;">${dvlp}</strong></span>` : ''}
          </div>
          ${isPinned ? '<div style="font-size: 10px; color: #64748b; margin-top: 4px; text-align: right;">(Pinned • Click outside to close)</div>' : ''}
        </div>
      `;
    }

    if (fType === 'genesis_area') {
      const title = props.title || 'Tropical Disturbance';
      const p2 = props.prob_2day || '0%';
      const r2 = props.risk_2day || 'Low';
      const p7 = props.prob_7day || '0%';
      const r7 = props.risk_7day || 'Low';
      const level = props.formation_level || 'Low';
      const color = props.fill_color || '#eab308';

      return `
        <div style="font-family: inherit; font-size: 13px; color: #f8fafc; line-height: 1.4; padding: 2px;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; gap: 8px;">
            <span style="font-weight: 700; font-size: 13px; color: #fbbf24;">
              🌊 ${title}
            </span>
            <span style="background: ${color}; color: #000; font-weight: 700; font-size: 11px; padding: 2px 7px; border-radius: 4px; white-space: nowrap;">
              ${level} Risk
            </span>
          </div>
          <div style="font-size: 11px; color: #cbd5e1; margin-bottom: 4px;">
            <strong>7-Day Formation Probability:</strong> <span style="color: ${color}; font-weight: 700;">${p7}</span> (${r7} risk)
          </div>
          <div style="font-size: 11px; color: #94a3b8; margin-bottom: 6px;">
            <strong>2-Day Formation Probability:</strong> ${p2} (${r2} risk)
          </div>
          <div style="font-size: 10px; color: #64748b; border-top: 1px solid rgba(255,255,255,0.12); padding-top: 4px;">
            Source: NOAA NHC Tropical Weather Outlook (GTWO)
          </div>
          ${isPinned ? '<div style="font-size: 10px; color: #64748b; margin-top: 4px; text-align: right;">(Pinned • Click outside to close)</div>' : ''}
        </div>
      `;
    }

    if (fType === 'watch_warning') {
      const title = props.title || 'Coastal Warning';
      const color = props.color || '#ef4444';
      return `
        <div style="font-family: inherit; font-size: 13px; color: #f8fafc; line-height: 1.4; padding: 2px;">
          <div style="font-weight: 700; font-size: 13px; color: ${color}; margin-bottom: 4px;">
            ⚠️ ${title}
          </div>
          <div style="font-size: 11px; color: #cbd5e1;">
            Official NOAA National Hurricane Center coastal alert in effect for coastal segments.
          </div>
          ${isPinned ? '<div style="font-size: 10px; color: #64748b; margin-top: 4px; text-align: right;">(Pinned • Click outside to close)</div>' : ''}
        </div>
      `;
    }

    if (fType === 'wind_radii') {
      const stormName = props.storm_name || 'Tropical Cyclone';
      const isInitial = props.is_initial !== false;
      const title = isInitial ? `Current Wind Field Estimate` : `Forecast Wind Swath (+${props.tau_hr ?? 0}h)`;
      const forceName = props.force_name || (props.radii_kt >= 64 ? 'Hurricane Force' : (props.radii_kt >= 50 ? 'Storm Force' : 'Tropical Storm Force'));
      const fillColor = props.fill_color || '#eab308';
      const rKt = props.radii_kt || 34;
      const rMph = props.radii_mph || Math.round(rKt * 1.15078);
      const quads = props.quadrant_extents || '';
      const maxExt = props.max_extent_nm ? `${props.max_extent_nm} nm` : '';

      return `
        <div style="font-family: inherit; font-size: 13px; color: #f8fafc; line-height: 1.4; padding: 2px; min-width: 220px;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; gap: 8px;">
            <span style="font-weight: 800; font-size: 13px; color: #f59e0b;">
              💨 ${stormName}
            </span>
            <span style="background: ${fillColor}; color: #000; font-weight: 800; font-size: 11px; padding: 2px 7px; border-radius: 4px; white-space: nowrap;">
              ${rKt} kt
            </span>
          </div>

          <div style="font-size: 12px; font-weight: 800; color: #f8fafc; margin-bottom: 2px;">
            ${title}
          </div>
          <div style="font-size: 11px; font-weight: 700; color: ${fillColor}; margin-bottom: 6px;">
            ${forceName} (${rKt}+ kt / ${rMph}+ mph)
          </div>

          <div style="font-size: 11px; color: #cbd5e1; margin-bottom: 5px;">
            ${props.description || `Sustained winds of ${rKt}+ kt extend outward in asymmetric quadrants.`}
          </div>

          ${quads ? `
            <div style="background: rgba(15, 23, 42, 0.6); padding: 5px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.08); font-size: 11px; color: #94a3b8; margin-bottom: 4px;">
              <strong style="color: #cbd5e1; display: block; margin-bottom: 2px;">Quadrant Radii (from center):</strong>
              ${quads}
              ${maxExt ? `<div style="margin-top: 2px; color: #38bdf8; font-weight: 600;">Max Radius: ${maxExt}</div>` : ''}
            </div>
          ` : ''}

          <div style="font-size: 10px; color: #64748b; border-top: 1px solid rgba(255,255,255,0.12); padding-top: 4px;">
            NOAA National Hurricane Center Advisory Extents
          </div>
          ${isPinned ? '<div style="font-size: 10px; color: #64748b; margin-top: 4px; text-align: right;">(Pinned • Click outside to close)</div>' : ''}
        </div>
      `;
    }

    return `
      <div style="font-family: inherit; font-size: 12px; color: #f8fafc; padding: 2px;">
        <strong>${props.title || 'Tropical Feature'}</strong>
        ${props.description ? `<div style="font-size: 11px; color: #cbd5e1; margin-top: 4px;">${props.description}</div>` : ''}
      </div>
    `;
  }
}

export default TropicalLayer;
